// src/pages/Appli/AppliAdmin/Pages/Partenaires/Partenaires.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getAllPartenaires,
  addPartenaire,
  updatePartenaire,
  deletePartenaire,
} from "../../../../services/partenairesServices";

import Button from "../../../../components/Button/Button";
import ButtonEdit from "../../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../../components/Button/ButtonDelete";

import EditPartenairesModale from "./EditPartenairesModale";
import CreatePartenairesModale from "./CreatePartenairesModale";

import { FaPlus } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const toAbs = (url) => {
  if (!url) return "";
  return isExternalURL(url) ? url : `${BASE_URL}${url}`;
};

const Partenaires = () => {
  const [partenaires, setPartenaires] = useState([]);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [currentPartenaire, setCurrentPartenaire] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const ui = useMemo(() => {
    const card =
      "rounded-3xl border border-black/10 bg-white/80 shadow-sm backdrop-blur " +
      "dark:bg-white/5 dark:border-white/10";
    const title = "text-gray-900 dark:text-white";
    const muted = "text-gray-600 dark:text-gray-300";
    const chip =
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold " +
      "border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20";
    const btnIcon =
      "w-9 h-9 inline-flex items-center justify-center rounded-2xl " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 hover:bg-white dark:hover:bg-black/30 transition";
    return { card, title, muted, chip, btnIcon };
  }, []);

  const checkUserRole = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.role_id === 4) setIsDemoMode(true);
  };

  const guardDemo = () => {
    if (isDemoMode) {
      toast.info("Fonction désactivée en mode Démonstration");
      return true;
    }
    return false;
  };

  const fetchPartenaires = async () => {
    try {
      const data = await getAllPartenaires();
      setPartenaires(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des partenaires :", error);
      toast.error("Erreur lors de la récupération des partenaires.");
    }
  };

  useEffect(() => {
    fetchPartenaires();
    checkUserRole();
  }, []);

  const handleCreate = async (payload) => {
    if (guardDemo()) return;
    try {
      await addPartenaire(payload);
      toast.success("Partenaire créé ✅");
      await fetchPartenaires();
    } catch (error) {
      console.error("Erreur création partenaire :", error);
      toast.error("Erreur lors de la création du partenaire.");
    }
  };

  const handleOpenEdit = (partenaire) => {
    if (guardDemo()) return;
    setCurrentPartenaire(partenaire);
    setEditModalOpen(true);
  };

  const handleSave = async (payload) => {
    if (guardDemo()) return;
    try {
      await updatePartenaire(payload);
      toast.success("Partenaire modifié ✅");
      await fetchPartenaires();
      setEditModalOpen(false);
      setCurrentPartenaire(null);
    } catch (error) {
      console.error("Erreur modification partenaire :", error);
      toast.error("Erreur lors de la modification du partenaire.");
    }
  };

  const handleDelete = async (id) => {
    if (guardDemo()) return;
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce partenaire ?")) return;

    try {
      await deletePartenaire(id);
      toast.success("Partenaire supprimé ✅");
      await fetchPartenaires();
    } catch (error) {
      console.error("Erreur suppression partenaire :", error);
      toast.error("Erreur lors de la suppression du partenaire.");
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className={`${ui.card} p-5 sm:p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className={`text-2xl sm:text-3xl font-extrabold ${ui.title}`}>
                Gestion des Partenaires
              </h1>
              <p className={`mt-1 text-sm ${ui.muted}`}>
                Ajoute, modifie et organise tes partenaires (logos + liens).
              </p>
            </div>

            <Button
              text={
                <>
                  <FaPlus className="mr-2" /> Créer un partenaire
                </>
              }
              onClick={() => {
                if (!guardDemo()) setCreateModalOpen(true);
              }}
              className="gap-2"
            />
          </div>
        </div>

        {/* Desktop table */}
        <div className="mt-6 hidden lg:block">
          <div className={`${ui.card} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-black/5 dark:bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Logo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Nom
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Lien
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Description
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Ordre
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-black/5 dark:divide-white/10">
                  {partenaires.map((p) => (
                    <tr
                      key={p.id}
                      className={
                        (p.is_active
                          ? "bg-transparent"
                          : "bg-black/5 dark:bg-white/5") +
                        " hover:bg-black/5 dark:hover:bg-white/5"
                      }
                    >
                      <td className="px-4 py-3">
                        <div className="w-24 h-16 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 flex items-center justify-center overflow-hidden">
                          {p.photo_url ? (
                            <img
                              src={toAbs(p.photo_url)}
                              alt={p.nom || "logo"}
                              className="max-h-14 max-w-[88px] object-contain"
                              loading="lazy"
                            />
                          ) : (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Sans logo
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">
                        {p.nom || "—"}
                      </td>

                      <td className="px-4 py-3 text-sm">
                        {p.link_url ? (
                          <a
                            href={p.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline dark:text-blue-400 break-all"
                          >
                            {p.link_url}
                          </a>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">—</span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        <div className="max-w-[520px] line-clamp-3">
                          {p.description || (
                            <span className="text-gray-500 dark:text-gray-400">
                              Description non disponible
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span className={ui.chip}>{Number(p.ordre ?? 0)}</span>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span className={ui.chip}>{p.is_active ? "Actif" : "Inactif"}</span>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <ButtonEdit onClick={() => handleOpenEdit(p)} />
                          <ButtonDelete onClick={() => handleDelete(p.id)} />
                        </div>
                      </td>
                    </tr>
                  ))}

                  {!partenaires.length && (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-600 dark:text-gray-300">
                        Aucun partenaire pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="mt-6 lg:hidden space-y-4">
          {partenaires.map((p) => (
            <div key={p.id} className={`${ui.card} p-5`}>
              <div className="flex items-start gap-4">
                <div className="w-20 h-16 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 flex items-center justify-center overflow-hidden shrink-0">
                  {p.photo_url ? (
                    <img
                      src={toAbs(p.photo_url)}
                      alt={p.nom || "logo"}
                      className="max-h-14 max-w-[72px] object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-xs text-gray-500 dark:text-gray-400">Sans logo</span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className={`text-base font-extrabold ${ui.title} truncate`}>
                      {p.nom || "—"}
                    </div>
                    <span className={ui.chip}>{p.is_active ? "Actif" : "Inactif"}</span>
                  </div>

                  {p.link_url ? (
                    <a
                      href={p.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline dark:text-blue-400 break-all"
                    >
                      {p.link_url}
                    </a>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400">—</div>
                  )}

                  <div className="mt-2 text-sm text-gray-700 dark:text-gray-200 line-clamp-3">
                    {p.description || (
                      <span className="text-gray-500 dark:text-gray-400">
                        Description non disponible
                      </span>
                    )}
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <span className={ui.chip}>Ordre: {Number(p.ordre ?? 0)}</span>

                    <div className="flex gap-2">
                      <ButtonEdit onClick={() => handleOpenEdit(p)} />
                      <ButtonDelete onClick={() => handleDelete(p.id)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {!partenaires.length && (
            <div className={`${ui.card} p-6 text-center text-sm ${ui.muted}`}>
              Aucun partenaire pour le moment.
            </div>
          )}
        </div>

        {/* Modales */}
        {isEditModalOpen && currentPartenaire && (
          <EditPartenairesModale
            partenaire={currentPartenaire}
            onClose={() => {
              setEditModalOpen(false);
              setCurrentPartenaire(null);
            }}
            onSave={handleSave}
          />
        )}

        {isCreateModalOpen && (
          <CreatePartenairesModale
            onCreate={async (newPartenaire) => {
              await handleCreate(newPartenaire);
              setCreateModalOpen(false);
            }}
            onClose={() => setCreateModalOpen(false)}
          />
        )}

        <ToastContainer position="bottom-right" />
      </div>
    </div>
  );
};

export default Partenaires;
